import type { StatusTone } from "@/components/ui";
import type { Priority, TaskAction, TaskStatus } from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

export function taskStatusTone(status: TaskStatus): StatusTone {
  switch (status) {
    case "created":
      return "info";
    case "assigned":
    case "in_progress":
      return "warning";
    case "blocked":
    case "pending_approval":
      return "danger";
    case "completed":
      return "success";
    case "cancelled":
    case "reopened":
      return "neutral";
    default:
      return "neutral";
  }
}

export function priorityTone(priority: Priority): StatusTone {
  switch (priority) {
    case "low":
    case "normal":
      return "neutral";
    case "high":
      return "warning";
    case "urgent":
    case "critical":
      return "danger";
    default:
      return "neutral";
  }
}

export const TASK_ACTIONS_FROM: Record<
  TaskAction,
  readonly TaskStatus[]
> = {
  assign: ["created", "reopened"],
  start: ["assigned", "reopened"],
  block: ["assigned", "in_progress", "pending_approval"],
  unblock: ["blocked"],
  mark_pending_approval: ["assigned", "in_progress", "blocked"],
  complete: ["assigned", "in_progress", "pending_approval", "blocked"],
  cancel: [
    "created",
    "assigned",
    "in_progress",
    "blocked",
    "pending_approval",
    "reopened",
  ],
  reopen: ["completed", "cancelled"],
};

export function allowedTaskActions(status: TaskStatus): TaskAction[] {
  return (Object.keys(TASK_ACTIONS_FROM) as TaskAction[]).filter((action) =>
    TASK_ACTIONS_FROM[action].includes(status),
  );
}

export function toIsoUtc(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toISOString();
}

/** Map linked entity_type to an HQ path when known — no invented destinations. */
export function entityDrilldownPath(
  entityType: string,
  entityId: string,
): string | null {
  const key = entityType.trim().toLowerCase().replaceAll(" ", "_");
  const map: Record<string, (id: string) => string> = {
    user: (id) => `/app/users/${id}`,
    users: (id) => `/app/users/${id}`,
    business: (id) => `/app/businesses/${id}`,
    businesses: (id) => `/app/businesses/${id}`,
    booking: (id) => `/app/bookings/${id}`,
    bookings: (id) => `/app/bookings/${id}`,
    community: (id) => `/app/communities/${id}`,
    communities: (id) => `/app/communities/${id}`,
    support_ticket: (id) => `/app/support/tickets/${id}`,
    moderation_case: (id) => `/app/trust-safety/cases/${id}`,
    marketplace_health_signal: (id) => `/app/marketplace/signals/${id}`,
    marketplace_opportunity: (id) => `/app/marketplace/opportunities/${id}`,
    knowledge_base_item: (id) => `/app/knowledge/articles/${id}`,
    report: (id) => `/app/trust-safety/reports/${id}`,
    refund: (id) => `/app/finance/refunds/${id}`,
    payout: (id) => `/app/finance/payouts/${id}`,
    financial_dispute: (id) => `/app/finance/disputes/${id}`,
    financial_exception: (id) => `/app/finance/exceptions/${id}`,
    transaction: (id) => `/app/finance/transactions/${id}`,
    creator_application: (id) => `/app/creators/applications/${id}`,
    creator_profile: (id) => `/app/creators/profiles/${id}`,
    approval: (id) => `/app/approvals/${id}`,
    task: (id) => `/app/tasks/${id}`,
    policy: (id) => `/app/knowledge/policies/${id}`,
  };
  const builder = map[key];
  return builder ? builder(entityId) : null;
}

/** "3h overdue", "due in 2d", "due today" — for a queue row. */
export function dueLabel(dueAt: string | null, closed: boolean, now = Date.now()): string {
  if (!dueAt) return "No due date";
  const diff = new Date(dueAt).getTime() - now;
  const abs = Math.abs(diff);
  const h = Math.round(abs / 3_600_000);
  const span = h < 1 ? "<1h" : h < 48 ? `${h}h` : `${Math.round(h / 24)}d`;
  if (closed) return new Date(dueAt).toLocaleString();
  return diff < 0 ? `${span} overdue` : `due in ${span}`;
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export const SOURCE_LABEL: Record<string, string> = {
  support_ticket: "Support ticket",
  report: "Report",
  moderation_case: "Moderation case",
  refund: "Refund",
  payout: "Payout",
  financial_dispute: "Financial dispute",
  business: "Business application",
  creator_application: "Creator application",
  document: "Document review",
  approval: "Approval",
};

export function isClosedStatus(status: string): boolean {
  return status === "completed" || status === "cancelled";
}
